import { assertEuro, normalizeLineDiscount } from './quoteFinancialInput';
import { orderedItems, orderedSections } from './quote-line-structure';
import { defaultQuoteSettings } from './quoteUiOptions';
import { optionsPayload, readOptionsState } from './quoteOptionsState';
import { defaultQuoteExpenses, withRequiredQuoteExpenses } from './quoteFinancialDefaults';
import {
  QUOTE_VERSION_STATUS,
} from './quoteVersionStatus';

export const quoteTypes = [['commercial', 'Devis commercial'], ['revision', 'Révision'], ['variant', 'Variante'], ['option', 'Option'], ['amendment', 'Avenant']];
export const quoteCommercialStatuses = [
  ['draft', 'En préparation'],
  ['internal_review', 'Validation interne'],
  ['sent', 'Envoyé'],
  ['accepted', 'Accepté'],
  ['rejected', 'Refusé'],
  ['expired', 'Expiré'],
  ['cancelled', 'Annulé'],
];
const COMMERCIAL_STATUS_ALIASES = { 'En cours': 'draft', 'Envoyé': 'sent', 'Commandé': 'accepted' };

const moneyFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatMoney = (value, currencyCode = 'EUR') => currencyCode === 'EUR'
  ? moneyFormatter.format(Number(value || 0))
  : `${Number(value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currencyCode}`;

export const getProjectName = (project) => project?.name || project?.label || project?.code || '-';

export const getQuoteProject = (quote) => quote?.Project || quote?.project || null;

export const createLine = (overrides = {}) => ({
  id: `line-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  description: '',
  lineComment: '',
  quantity: 1,
  unit: 'Ens',
  unitPrice: 0,
  estimatedHours: 0,
  laborRate: defaultQuoteSettings.defaultLaborRate,
  laborProfileId: null,
  laborProfileCode: '',
  laborProfileLabel: '',
  otherDirectCost: 0,
  purchaseCost: 0,
  subcontractingCost: 0,
  marginCoefficient: defaultQuoteSettings.defaultMarginCoefficient,
  inheritedCoefficient: defaultQuoteSettings.defaultMarginCoefficient,
  calculatedCoefficient: defaultQuoteSettings.defaultMarginCoefficient,
  manualCoefficient: null,
  effectiveCoefficient: defaultQuoteSettings.defaultMarginCoefficient,
  coefficientMode: 'inherited',
  sellingPriceMode: 'calculated',
  forcedUnitSellingPrice: null,
  vatRate: defaultQuoteSettings.defaultVatRate,
  discountKind: 'percent',
  discountValue: 0,
  weightingCoefficient: 1,
  isClientVisible: true,
  lineType: 'service',
  manualAllocatedExpenseAmount: 0,
  planningSourceKey: null,
  planningResourceUserId: null,
  planningResourceName: null,
  planningResourceManual: false,
  planningPhaseCode: null,
  planningLotCode: null,
  planningLevelCode: null,
  planningDocumentTypeCode: null,
  planningStartDate: null,
  planningEndDate: null,
  planningEstimatedHours: null,
  ...overrides,
});



const PARTNER_PARAMETER_DEFINITIONS = [
  ['laborCoefficient', 'Coefficient main-d’œuvre'],
  ['materialCoefficient', 'Coefficient matériel'],
  ['subcontractingCoefficient', 'Coefficient sous-traitance'],
  ['coContractingCoefficient', 'Coefficient co-traitance'],
  ['materialPurchaseDiscountRate', 'Remise achat matériel partenaire'],
];

const mergePartnerParameters = (parameters = [], terms = {}, companyId = '') => {
  const byCode = new Map(parameters.map((parameter) => [parameter.parameterCode, parameter]));
  PARTNER_PARAMETER_DEFINITIONS.forEach(([code, label]) => {
    const value = terms?.[code];
    if (value === '' || value === null || value === undefined) return;
    const previous = byCode.get(code) || {};
    byCode.set(code, {
      ...previous,
      parameterCode: code,
      parameterLabel: previous.parameterLabel || label,
      parameterType: 'number',
      valueNumber: value,
      sourceReference: `partner:${companyId}:quote-settings`,
      orderIndex: previous.orderIndex ?? byCode.size,
    });
  });
  return [...byCode.values()];
};

export const validityDateFromDays = (days, baseDate = new Date()) => {
  const count = Number(days);
  if (days == null || days === '' || !Number.isInteger(count) || count < 0) return '';
  const date = new Date(baseDate);
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + count);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

export const applyPartnerQuoteSettings = (editor, rawTerms = {}, companyId = '') => {
  if (!rawTerms || rawTerms.enabled === false) return editor;
  const terms = rawTerms || {};
  const next = {
    ...editor,
    companyId: companyId || editor.companyId,
    paymentTerms: terms.paymentTerms ?? terms.payment_terms ?? editor.paymentTerms,
    paymentDelayDays: terms.paymentDelayDays ?? terms.payment_delay_days ?? editor.paymentDelayDays,
    depositRate: terms.depositRate ?? terms.deposit_rate ?? editor.depositRate,
    globalDiscountKind: terms.globalDiscountKind ?? terms.global_discount_kind ?? editor.globalDiscountKind,
    globalDiscountValue: terms.globalDiscountValue ?? terms.global_discount_value ?? editor.globalDiscountValue,
    defaultVatRate: terms.defaultVatRate ?? terms.default_vat_rate ?? editor.defaultVatRate,
    currencyCode: terms.currencyCode ?? terms.currency_code ?? editor.currencyCode,
    partnerMaterialPurchaseDiscountRate: terms.materialPurchaseDiscountRate
      ?? terms.material_purchase_discount_rate
      ?? editor.partnerMaterialPurchaseDiscountRate
      ?? 0,
    partnerQuoteSettingsSource: companyId ? `partner:${companyId}:quote-settings` : editor.partnerQuoteSettingsSource,
    serverCalculationStatus: 'stale',
  };
  const validityDays = terms.validityDays ?? terms.validity_days;
  if (validityDays !== '' && validityDays !== null && validityDays !== undefined) {
    next.validityDays = Number(validityDays);
  }
  next.versionParameters = mergePartnerParameters(editor.versionParameters || [], {
    laborCoefficient: terms.laborCoefficient ?? terms.labor_coefficient,
    materialCoefficient: terms.materialCoefficient ?? terms.material_coefficient,
    subcontractingCoefficient: terms.subcontractingCoefficient ?? terms.subcontracting_coefficient,
    coContractingCoefficient: terms.coContractingCoefficient ?? terms.co_contracting_coefficient,
    materialPurchaseDiscountRate: terms.materialPurchaseDiscountRate ?? terms.material_purchase_discount_rate,
  }, companyId);
  return next;
};

export const flattenEditorLines = (sections = []) =>
  sections.flatMap((section) => section.lines || []);

const PLANNING_PHASE_LABELS = {
  CON: 'Conception',
  DCE: 'DCE',
  EXE: 'Exécution',
  DOE: 'DOE',
  VISA: 'VISA',
  AUTRE: 'Autres prestations',
};

export const planningDraftToQuoteSections = (draft, editor = {}) => {
  const stamp = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const sections = [];
  (draft?.phases || []).forEach((phase, phaseIndex) => {
    const phaseId = `planning-${stamp}-phase-${phaseIndex}`;
    const phaseLabel = PLANNING_PHASE_LABELS[phase.code] || phase.code || 'Prestations';
    const phaseSection = {
      id: phaseId,
      parentSectionId: null,
      code: phase.code || '',
      title: phaseLabel,
      description: 'Charge issue du planning AO / DCE.',
      isClientVisible: true,
      orderIndex: phaseIndex,
      lines: [],
    };
    sections.push(phaseSection);
    (phase.lots || []).forEach((lot, lotIndex) => {
      const usePhaseDirectly = lot.code === 'GENERAL' && (phase.lots || []).length === 1;
      const sectionId = usePhaseDirectly ? phaseId : `planning-${stamp}-phase-${phaseIndex}-lot-${lotIndex}`;
      let target = phaseSection;
      if (!usePhaseDirectly) {
        target = {
          id: sectionId,
          parentSectionId: phaseId,
          code: lot.code === 'GENERAL' ? '' : lot.code,
          title: lot.code === 'GENERAL' ? 'Prestations générales' : lot.code,
          description: '',
          isClientVisible: true,
          orderIndex: lotIndex,
          lines: [],
        };
        sections.push(target);
      }
      const studiesProfile = draft?.defaultLaborProfile || {};
      target.lines = (lot.items || []).map((item) => {
        const estimatedHours = Number(item.estimatedHours || 0);
        const laborRate = Number(studiesProfile.costHourlyRate ?? editor.defaultLaborRate ?? defaultQuoteSettings.defaultLaborRate);
        return createLine({
          quoteSectionId: sectionId,
          description: item.description || 'Prestation',
          quantity: 1,
          unit: 'U',
          estimatedHours,
          laborRate,
          laborProfileId: studiesProfile.id || null,
          laborProfileCode: studiesProfile.code || 'ETUDES',
          laborProfileLabel: studiesProfile.label || 'Études',
          laborItems: [{
            laborProfileId: studiesProfile.id || null,
            profileCodeSnapshot: studiesProfile.code || 'ETUDES',
            profileLabelSnapshot: studiesProfile.label || 'Études',
            resourceQuantity: 1,
            unitHours: estimatedHours,
            costHourlyRateSnapshot: laborRate,
            sellingHourlyRateSnapshot: studiesProfile.suggestedSellingHourlyRate ?? null,
            rateMode: studiesProfile.id ? 'profile' : 'snapshot',
          }],
          marginCoefficient: Number(editor.defaultMarginCoefficient ?? defaultQuoteSettings.defaultMarginCoefficient),
          inheritedCoefficient: Number(editor.defaultMarginCoefficient ?? defaultQuoteSettings.defaultMarginCoefficient),
          calculatedCoefficient: Number(editor.defaultMarginCoefficient ?? defaultQuoteSettings.defaultMarginCoefficient),
          effectiveCoefficient: Number(editor.defaultMarginCoefficient ?? defaultQuoteSettings.defaultMarginCoefficient),
          vatRate: Number(editor.defaultVatRate ?? defaultQuoteSettings.defaultVatRate),
          sourceCatalogReference: item.planningKey ? `affair-planning:${item.planningKey}` : null,
          planningSourceKey: item.planningKey || null,
          planningPhaseCode: item.phaseCode || null,
          planningLotCode: item.lotCode || null,
          planningLevelCode: item.levelCode || null,
          planningDocumentTypeCode: item.documentTypeCode || null,
          planningStartDate: item.startDate || null,
          planningEndDate: item.endDate || null,
          planningEstimatedHours: estimatedHours,
          planningResourceUserId: item.assignedResourceId ?? null,
          planningResourceName: item.assignedResourceName || null,
          planningResourceManual: Boolean(item.assignedResourceManual),
        });
      });
    });
  });
  return sections;
};

export const emptyEditor = (projectId = '', affairId = '') => {
  const sectionId = `section-${Date.now()}`;
  return ({
  id: null,
  quoteVersionId: null,
  versionStatus: QUOTE_VERSION_STATUS.DRAFT,
  versionNumber: 1,
  commercialStatus: 'draft',
  permissions: {
    canEdit: true,
    canForcePrice: false,
    canForceCoefficient: false,
    canValidate: false,
    canReopen: false,
    canIssue: false,
    canClone: false,
    canSend: false,
    canViewMinute: true,
    canViewCosts: true,
  },
  quoteNumber: '',
  title: 'Nouveau devis',
  status: 'Brouillon',
  quoteType: 'commercial',
  projectId: projectId || '',
  affairId: affairId || '',
  companyId: '',
  objectText: '',
  scopeText: '',
  exclusionsText: '',
  paymentTerms: '',
  paymentDelayDays: 30,
  depositRate: 0,
  globalDiscountKind: 'percent',
  globalDiscountValue: 0,
  validityDays: 30,
  validUntil: null,
  executionDelay: '',
  deliveryFormatCode: 'PDF_ONLY',
  deliveryFormatOther: '',
  siteVisitsIncluded: false,
  siteVisitCount: '',
  siteVisitConditions: '',
  paymentSchedule: [],
  notesClient: '',
  notesInternal: '',
  currencyCode: 'EUR',
  versionParameters: [],
  versionExpenses: defaultQuoteExpenses(),
  options: [],
  optionsDirty: false,
  optionsOriginalState: 'missing',
  serverCalculation: null,
  serverCalculationStatus: 'local_only',
  defaultExpenseAllocationMode: 'cost',
  customerSnapshot: {},
  partnerQuoteSettingsSource: '',
  partnerMaterialPurchaseDiscountRate: 0,
  ...defaultQuoteSettings,
  primarySectionId: sectionId,
  sections: [{
    id: sectionId,
    parentSectionId: null,
    title: 'Lignes du devis',
    orderIndex: 0,
    lines: [],
  }],
  });
};

const MANUAL_LINE_COST_MARKER = '__MANUAL_LINE_COST__';
const manualMaterialItem = (line) => (line.materials || []).find((item) => item.reference === MANUAL_LINE_COST_MARKER);
const manualSubcontractingItem = (line) => (line.subcontractingItems || []).find((item) => item.description === MANUAL_LINE_COST_MARKER);

export const lineFromApi = (line, index) => createLine({
  id: line.id || `line-${index}`,
  orderIndex: line.orderIndex ?? line.order_index,
  quoteSectionId: line.quoteSectionId || line.quote_section_id || null,
  description: line.description || '',
  lineComment: line.lineComment ?? line.line_comment ?? '',
  quantity: line.quantity ?? 1,
  unit: line.unit || '',
  unitPrice: line.unitPrice ?? line.unit_price ?? 0,
  sellingPriceHT: line.totalEffectiveSellingPrice ?? line.sellingPriceHT,
  vatAmount: line.vatAmount,
  sellingPriceTTC: line.totalSellingPriceTTC ?? line.sellingPriceTTC,
  estimatedHours: line.estimatedHours
    ?? line.estimated_hours
    ?? line.laborItems?.[0]?.unitHours
    ?? 0,
  laborRate: line.laborRate
    ?? line.labor_rate
    ?? line.laborItems?.[0]?.costHourlyRateSnapshot
    ?? defaultQuoteSettings.defaultLaborRate,
  laborProfileId: line.laborProfileId ?? line.labor_profile_id ?? line.laborItems?.[0]?.laborProfileId ?? null,
  laborProfileCode: line.laborProfileCode ?? line.labor_profile_code ?? line.laborItems?.[0]?.profileCodeSnapshot ?? '',
  laborProfileLabel: line.laborProfileLabel ?? line.labor_profile_label ?? line.laborItems?.[0]?.profileLabelSnapshot ?? '',
  otherDirectCost: line.otherDirectCost ?? line.other_direct_cost ?? 0,
  purchaseCost: line.purchaseCost ?? line.purchase_cost ?? manualMaterialItem(line)?.netUnitPurchasePrice ?? manualMaterialItem(line)?.catalogUnitPurchasePrice ?? line.unitPrice ?? line.unit_price ?? 0,
  subcontractingCost: line.subcontractingCost ?? line.subcontracting_cost ?? manualSubcontractingItem(line)?.unitPurchasePrice ?? 0,
  marginCoefficient: line.marginCoefficient ?? line.margin_coefficient ?? defaultQuoteSettings.defaultMarginCoefficient,
  inheritedCoefficient: line.inheritedCoefficient ?? line.inherited_coefficient ?? null,
  calculatedCoefficient: line.calculatedCoefficient ?? line.calculated_coefficient ?? line.marginCoefficient ?? line.margin_coefficient ?? null,
  manualCoefficient: line.manualCoefficient ?? line.manual_coefficient ?? null,
  forcedCoefficient: line.forcedCoefficient ?? line.forced_coefficient ?? null,
  effectiveCoefficient: line.effectiveCoefficient ?? line.effective_coefficient ?? line.marginCoefficient ?? line.margin_coefficient ?? null,
  coefficientMode: line.coefficientMode || line.coefficient_mode || 'inherited',
  calculatedUnitSellingPrice: line.calculatedUnitSellingPrice ?? line.calculated_unit_selling_price ?? 0,
  forcedUnitSellingPrice: line.forcedUnitSellingPrice ?? line.forced_unit_selling_price ?? null,
  effectiveUnitSellingPrice: line.effectiveUnitSellingPrice ?? line.effective_unit_selling_price ?? 0,
  sellingPriceMode: line.sellingPriceMode || line.selling_price_mode || 'calculated',
  forceReason: line.forceReason || line.force_reason || '',
  forcedAt: line.forcedAt || line.forced_at || null,
  forceExpiresAt: line.forceExpiresAt || line.force_expires_at || null,
  forcedBy: line.forcedBy ?? line.forced_by ?? line.forcedByUser?.id ?? line.forced_by_user?.id ?? null,
  forcedByUser: line.forcedByUser || line.forced_by_user || null,
  vatRate: line.vatRate ?? line.vat_rate ?? defaultQuoteSettings.defaultVatRate,
  discountKind: line.discountKind !== undefined ? line.discountKind : line.discount_kind ?? null,
  discountValue: line.discountValue !== undefined ? line.discountValue : line.discount_value ?? null,
  weightingCoefficient: line.weightingCoefficient ?? line.weighting_coefficient ?? 1,
  isClientVisible: line.isClientVisible ?? line.is_client_visible ?? true,
  lineType: line.lineType || line.line_type || 'service',
  manualAllocatedExpenseAmount: line.manualAllocatedExpenseAmount ?? line.manual_allocated_expense_amount ?? 0,
  sourceCatalogReference: line.sourceCatalogReference ?? line.source_catalog_reference ?? null,
  planningSourceKey: line.planningSourceKey ?? line.planning_source_key ?? null,
  planningPhaseCode: line.planningPhaseCode ?? line.planning_phase_code ?? null,
  planningLotCode: line.planningLotCode ?? line.planning_lot_code ?? null,
  planningLevelCode: line.planningLevelCode ?? line.planning_level_code ?? null,
  planningDocumentTypeCode: line.planningDocumentTypeCode ?? line.planning_document_type_code ?? null,
  planningStartDate: line.planningStartDate ?? line.planning_start_date ?? null,
  planningEndDate: line.planningEndDate ?? line.planning_end_date ?? null,
  planningEstimatedHours: line.planningEstimatedHours ?? line.planning_estimated_hours ?? null,
  planningResourceUserId: line.planningResourceUserId ?? line.planning_resource_user_id ?? null,
  planningResourceName: line.planningResourceName ?? line.planning_resource_name ?? null,
  planningResourceManual: Boolean(line.planningResourceManual ?? line.planning_resource_manual),
  laborItems: line.laborItems || [],
  materials: (line.materials || []).filter((item) => item.reference !== MANUAL_LINE_COST_MARKER),
  subcontractingItems: (line.subcontractingItems || []).filter((item) => item.description !== MANUAL_LINE_COST_MARKER),
});

const DEPRECATED_EXPENSE_LINE_TYPES = new Set(['fixed_fee', 'indirect_fee']);

const legacyLineExpense = (line, index, allocationMode) => {
  const quantity = Number(line.quantity ?? 1);
  const hours = Number(line.estimatedHours ?? line.estimated_hours ?? 0);
  const laborRate = Number(line.laborRate ?? line.labor_rate ?? 0);
  const purchaseCost = Number(
    line.purchaseCost ?? line.purchase_cost ?? line.unitPrice ?? line.unit_price ?? 0
  );
  const subcontractingCost = Number(
    line.subcontractingCost ?? line.subcontracting_cost ?? 0
  );
  const amount = Number(
    line.costPrice ?? line.cost_price
    ?? line.totalPrice ?? line.total_price
    ?? (quantity * ((hours * laborRate) + purchaseCost + subcontractingCost))
  ) || 0;

  return {
    _key: `legacy-line-expense-${line.id || index}`,
    labelSnapshot: line.description || 'Frais historique',
    categorySnapshot: 'legacy_line_expense',
    calculationType: 'fixed_amount',
    calculationBase: 'direct_cost',
    inputValue: amount,
    calculatedAmount: amount,
    effectiveAmount: amount,
    amountMode: 'calculated',
    allocationMode: allocationMode || 'cost',
    isClientVisible: false,
    allocations: [],
  };
};

const parameterValueFromQuote = (quote, code, fallback = '') => {
  const parameter = (quote.parameters || []).find((item) => item.parameterCode === code);
  if (!parameter) return fallback;
  return parameter.valueNumber ?? parameter.valueText ?? parameter.valueBoolean ?? fallback;
};

const parameterSourceFromQuote = (quote, code) => (
  (quote.parameters || []).find((item) => item.parameterCode === code)?.sourceReference || ''
);

const laborRateParameterValue = (parameters, code) => {
  if (!code) return undefined;
  const parameter = (parameters || []).find((item) => item.parameterCode === `laborRate.${code}`);
  return parameter?.valueNumber;
};

const applyVersionLaborRatesToLine = (line, parameters) => {
  const laborItems = Array.isArray(line.laborItems) ? line.laborItems.map((item) => {
    const code = item.profileCodeSnapshot || item.profileCode || line.laborProfileCode || '';
    const versionRate = laborRateParameterValue(parameters, code);
    if (versionRate === undefined || versionRate === null || item.rateMode === 'manual') return item;
    return { ...item, costHourlyRateSnapshot: versionRate, effectiveHourlyRate: versionRate, rateMode: 'profile' };
  }) : [];
  const code = line.laborProfileCode || laborItems[0]?.profileCodeSnapshot || '';
  const versionRate = laborRateParameterValue(parameters, code);
  const isManual = laborItems[0]?.rateMode === 'manual';
  return {
    ...line,
    laborRate: versionRate !== undefined && versionRate !== null && !isManual ? versionRate : line.laborRate,
    laborItems,
  };
};

export const lineOrderFingerprint = (editor) => {
  const { savedLineOrderFingerprint, savedLineOrderSequence, serverCalculationStatus, ...input } = editor;
  const sortKeys = (value) => {
    if (Array.isArray(value)) return value.map(sortKeys);
    if (!value || typeof value !== 'object') return value;
    return Object.fromEntries(Object.keys(value).sort().map((key) => [key, sortKeys(value[key])]));
  };
  return JSON.stringify(sortKeys({ ...input, sections: (input.sections || []).map((section) => ({ ...section,
    lines: [...(section.lines || [])].sort((a, b) => String(a.id).localeCompare(String(b.id)))
      .map(({ orderIndex, ...line }) => line),
  })) }));
};
const lineOrderSequence = (editor) => JSON.stringify((editor.sections || []).map((section) =>
  [section.id, (section.lines || []).map((line) => line.id)]));
export const isLineOrderOnlyEdit = (editor) => Boolean(editor.id && editor.quoteVersionId
  && editor.savedLineOrderFingerprint && editor.savedLineOrderSequence !== lineOrderSequence(editor)
  && editor.savedLineOrderFingerprint === lineOrderFingerprint(editor));

export const editorFromQuote = (quote) => {
  const flattenQuoteSections = (sections = [], parentSectionId = null) =>
    sections.flatMap((section) => {
      const normalized = {
        ...section,
        parentSectionId: section.parentSectionId || parentSectionId,
      };
      return [
        normalized,
        ...flattenQuoteSections(section.children || [], section.id),
      ];
    });
  const sourceSections = orderedSections(flattenQuoteSections(quote.sections || []));
  const sourceLines = sourceSections
    .flatMap((section) => section.lines || section.QuoteLines || []);
  const allocationMode = quote.version?.ventilationMode
    || quote.version?.ventilation_mode
    || quote.ventilationMode
    || quote.ventilation_mode
    || defaultQuoteSettings.defaultExpenseAllocationMode;
  const legacyExpenseLines = sourceLines.filter((line) =>
    DEPRECATED_EXPENSE_LINE_TYPES.has(line.lineType || line.line_type));
  const versionParameters = quote.parameters || [];

  const editor = ({
  id: quote.id,
  quoteVersionId: quote.quoteVersionId || quote.quote_version_id || quote.currentVersionId || quote.current_version_id || null,
  contentSchemaVersion: Number(quote.contentSchemaVersion || 1),
  composition: quote.composition || null,
  clientDocumentV2: quote.clientDocumentV2 || null,
  versionStatus: quote.version?.status || QUOTE_VERSION_STATUS.DRAFT,
  versionNumber: quote.version?.versionNumber || quote.version?.version_number || 1,
  createdAt: quote.version?.createdAt || quote.createdAt || null,
  revision: quote.version?.revision ?? quote.revision ?? null,
  currentVersionId: quote.currentVersionId || quote.current_version_id || null,
  acceptedVersionId: quote.acceptedVersionId || quote.accepted_version_id || null,
  commercialStatus: quote.commercialStatus || quote.commercial_status || quote.status || 'draft',
  permissions: quote.permissions || {
    canEdit: !quote.id,
    canValidate: false,
    canIssue: false,
    canClone: false,
  },
  primarySectionId: sourceSections[0]?.id || null,
  sections: sourceSections.map((section) => ({
    id: section.id,
    parentSectionId: section.parentSectionId || null,
    code: section.code || '',
    title: section.title,
    description: section.description || '',
    isClientVisible: section.isClientVisible ?? section.is_client_visible ?? true,
    orderIndex: section.orderIndex,
    lines: orderedItems(section.lines || section.QuoteLines || [])
      .filter((line) => !DEPRECATED_EXPENSE_LINE_TYPES.has(line.lineType || line.line_type))
      .map((line, index) => applyVersionLaborRatesToLine(lineFromApi({
        ...line,
        quoteSectionId: line.quoteSectionId || line.quote_section_id || section.id,
      }, index), versionParameters)),
  })),
  originalLineIds: sourceLines
    .map((line) => line.id)
    .filter(Boolean),
  quoteNumber: quote.quoteNumber || quote.quote_number || '',
  title: quote.title || 'Devis',
  status: COMMERCIAL_STATUS_ALIASES[quote.commercialStatus || quote.commercial_status || quote.status]
    || quote.commercialStatus || quote.commercial_status || quote.status || 'Brouillon',
  quoteType: quote.quoteType || quote.quote_type || 'commercial',
  projectId: quote.projectId || quote.project_id || '',
  affairId: quote.affairId || quote.affair_id || '',
  companyId: quote.companyId || quote.company_id || '',
  objectText: quote.version?.objectText || '',
  scopeText: quote.version?.scopeText || '',
  exclusionsText: quote.version?.exclusionsText || '',
  paymentTerms: quote.version?.paymentTerms || '',
  paymentDelayDays: quote.version?.paymentDelayDays ?? '',
  depositRate: quote.version?.depositRate ?? 0,
  globalDiscountKind: quote.version?.globalDiscountKind ?? quote.version?.global_discount_kind ?? 'percent',
  globalDiscountValue: quote.version?.globalDiscountValue ?? quote.version?.global_discount_value ?? 0,
  validityDays: quote.version?.validityDays ?? quote.version?.validity_days ?? 30,
  validUntil: quote.version?.validUntil ?? quote.version?.valid_until ?? null,
  executionDelay: quote.version?.executionDelay || '',
  deliveryFormatCode: quote.version?.deliveryFormatCode || quote.version?.delivery_format_code || 'PDF_ONLY',
  deliveryFormatOther: quote.version?.deliveryFormatOther || quote.version?.delivery_format_other || '',
  siteVisitsIncluded: Boolean(quote.version?.siteVisitsIncluded ?? quote.version?.site_visits_included),
  siteVisitCount: quote.version?.siteVisitCount ?? quote.version?.site_visit_count ?? '',
  siteVisitConditions: quote.version?.siteVisitConditions || quote.version?.site_visit_conditions || '',
  paymentSchedule: (quote.paymentSchedule || []).map((item, index) => ({
    ...item,
    id: item.id || `payment-${index}`,
    orderIndex: item.orderIndex ?? index,
  })),
  notesClient: quote.version?.notesClient || '',
  notesInternal: quote.version?.notesInternal || '',
  currencyCode: quote.version?.currencyCode || 'EUR',
  versionParameters,
  partnerMaterialPurchaseDiscountRate: parameterValueFromQuote(quote, 'materialPurchaseDiscountRate', 0),
  partnerQuoteSettingsSource: parameterSourceFromQuote(quote, 'materialPurchaseDiscountRate'),
  options: Array.isArray(quote.options) ? quote.options : [],
  optionsDirty: false,
  optionsOriginalState: readOptionsState(quote.options, quote.optionsOriginalState),
  versionExpenses: Number(quote.contentSchemaVersion) === 2 ? (quote.expenses || []) : withRequiredQuoteExpenses([
    ...(quote.expenses || []),
    ...legacyExpenseLines.map((line, index) => legacyLineExpense(line, index, allocationMode)),
  ]),
  recordedFinancialPresentation: quote.financialPresentation || null,
  internalFinancialReport: quote.internalFinancialReport || null,
  clientDocument: quote.clientDocument || null,
  serverCalculation: quote.totals ? {
    totals: quote.totals,
    engineVersion: quote.totals.calculationEngineVersion
      || quote.totals.calculation_engine_version
      || null,
    calculatedAt: quote.totals.calculatedAt || quote.totals.calculated_at || null,
  } : null,
  serverCalculationStatus: quote.totals ? 'validated' : 'local_only',
  customerSnapshot: quote.customerSnapshot ? {
    customerCompanyId: quote.customerSnapshot.customerCompanyId ?? quote.customerSnapshot.companyId ?? null,
    customerContactId: quote.customerSnapshot.customerContactId ?? quote.customerSnapshot.contactId ?? null,
    customerLegalName: quote.customerSnapshot.customerLegalName ?? quote.customerSnapshot.legalName ?? '',
    customerTradeName: quote.customerSnapshot.customerTradeName ?? quote.customerSnapshot.tradeName ?? '',
    customerAddressLine1: quote.customerSnapshot.customerAddressLine1 ?? quote.customerSnapshot.addressLine1 ?? '',
    customerAddressLine2: quote.customerSnapshot.customerAddressLine2 ?? quote.customerSnapshot.addressLine2 ?? '',
    customerPostalCode: quote.customerSnapshot.customerPostalCode ?? quote.customerSnapshot.postalCode ?? '',
    customerCity: quote.customerSnapshot.customerCity ?? quote.customerSnapshot.city ?? '',
    customerCountry: quote.customerSnapshot.customerCountry ?? quote.customerSnapshot.country ?? '',
    customerRegistrationNumber: quote.customerSnapshot.customerRegistrationNumber ?? quote.customerSnapshot.registrationNumber ?? '',
    customerVatNumber: quote.customerSnapshot.customerVatNumber ?? quote.customerSnapshot.vatNumber ?? '',
    customerContactName: quote.customerSnapshot.customerContactName ?? quote.customerSnapshot.contactName ?? '',
    customerContactEmail: quote.customerSnapshot.customerContactEmail ?? quote.customerSnapshot.contactEmail ?? '',
    customerContactPhone: quote.customerSnapshot.customerContactPhone ?? quote.customerSnapshot.contactPhone ?? '',
  } : {},
  defaultLaborRate: quote.defaultLaborRate ?? quote.default_labor_rate ?? defaultQuoteSettings.defaultLaborRate,
  defaultVatRate: quote.version?.defaultVatRate ?? quote.version?.default_vat_rate ?? quote.defaultVatRate ?? quote.default_vat_rate ?? defaultQuoteSettings.defaultVatRate,
  defaultMarginCoefficient: quote.version?.defaultMarginCoefficient ?? quote.version?.default_margin_coefficient ?? quote.defaultMarginCoefficient ?? quote.default_margin_coefficient ?? defaultQuoteSettings.defaultMarginCoefficient,
  defaultExpenseAllocationMode: allocationMode,
  ventilationMode: quote.version?.ventilationMode || quote.version?.ventilation_mode || quote.ventilationMode || quote.ventilation_mode || defaultQuoteSettings.ventilationMode,
  roundingMode: quote.version?.roundingMode ?? quote.version?.rounding_mode ?? quote.roundingMode ?? quote.rounding_mode ?? defaultQuoteSettings.roundingMode,
  });
  editor.savedLineOrderFingerprint = lineOrderFingerprint(editor);
  editor.savedLineOrderSequence = lineOrderSequence(editor);
  return editor;
};

const serializeLineComponent = (line, item) => (
  Number.isSafeInteger(Number(line.id)) && Number(line.id) > 0
    ? item
    : { ...item, id: undefined, quoteLineId: undefined }
);

const serializeLine = (line, index) => ({
  id: Number.isSafeInteger(Number(line.id)) && Number(line.id) > 0 ? Number(line.id) : null,
  clientId: String(line.id ?? line.tempId ?? `line-${index}`),
  quoteSectionId: line.quoteSectionId || null,
  description: line.description,
  lineComment: line.lineComment,
  quantity: line.quantity,
  unit: line.unit,
  unitPrice: line.unitPrice,
  laborRate: line.laborRate,
  laborProfileId: line.laborProfileId || null,
  laborProfileCode: line.laborProfileCode || '',
  laborProfileLabel: line.laborProfileLabel || '',
  otherDirectCost: line.otherDirectCost || 0,
  purchaseCost: line.purchaseCost,
  subcontractingCost: line.subcontractingCost,
  marginCoefficient: line.marginCoefficient,
  inheritedCoefficient: line.inheritedCoefficient,
  calculatedCoefficient: line.calculatedCoefficient,
  manualCoefficient: line.manualCoefficient,
  coefficientMode: line.coefficientMode,
  forcedCoefficient: line.forcedCoefficient,
  effectiveCoefficient: line.effectiveCoefficient,
  sellingPriceMode: line.sellingPriceMode,
  forcedUnitSellingPrice: line.forcedUnitSellingPrice,
  forceReason: line.forceReason,
  forcedBy: line.forcedBy,
  forcedAt: line.forcedAt,
  forceExpiresAt: line.forceExpiresAt,
  ...(line.dpgfSource ? { dpgfSource: line.dpgfSource } : {}),
  totalPrice: line.totalPrice,
  estimatedHours: line.estimatedHours,
  totalEstimatedHours: line.totalEstimatedHours,
  weightingCoefficient: line.weightingCoefficient,
  baseCost: line.baseCost,
  laborCost: line.laborCost,
  costPrice: line.costPrice,
  preVentilationSellingPriceHT: line.preVentilationSellingPriceHT,
  saleBase: line.saleBase,
  isClientVisible: line.isClientVisible,
  lineType: line.lineType,
  allocatedExpenseAmount: line.allocatedExpenseAmount,
  allocatedFixedFees: line.allocatedFixedFees,
  manualAllocatedExpenseAmount: line.manualAllocatedExpenseAmount,
  sourceCatalogReference: line.sourceCatalogReference || (line.planningSourceKey ? `affair-planning:${line.planningSourceKey}` : null),
  planningSourceKey: line.planningSourceKey || null,
  planningPhaseCode: line.planningPhaseCode || null,
  planningLotCode: line.planningLotCode || null,
  planningLevelCode: line.planningLevelCode || null,
  planningDocumentTypeCode: line.planningDocumentTypeCode || null,
  planningStartDate: line.planningStartDate || null,
  planningEndDate: line.planningEndDate || null,
  planningEstimatedHours: line.planningEstimatedHours ?? line.estimatedHours ?? null,
  planningResourceUserId: line.planningResourceUserId ?? null,
  planningResourceName: line.planningResourceName || null,
  planningResourceManual: Boolean(line.planningResourceManual),
  finalTotalPrice: line.finalTotalPrice,
  sellingPriceHT: line.sellingPriceHT,
  vatRate: line.vatRate,
  ...normalizeLineDiscount(line),
  vatAmount: line.vatAmount,
  sellingPriceTTC: line.sellingPriceTTC,
  clientTotalPrice: line.clientTotalPrice,
  laborItems: (line.laborItems || []).map((item) => serializeLineComponent(line, item)),
  materials: (line.materials || []).filter((item) => item.reference !== MANUAL_LINE_COST_MARKER)
    .map((item) => serializeLineComponent(line, item)),
  subcontractingItems: (line.subcontractingItems || []).filter((item) => item.description !== MANUAL_LINE_COST_MARKER)
    .map((item) => serializeLineComponent(line, item)),
  orderIndex: index,
});

const serializeSections = (editor, calculated) => {
  const sections = editor.sections || [];
  const calculatedLines = new Map(
    calculated.internalLines.map((line) => [String(line.id), line])
  );

  if (!sections.length) {
    return [{
      id: editor.primarySectionId || null,
      clientId: String(editor.primarySectionId || 'primary-section'),
      title: 'Lignes du devis',
      orderIndex: 0,
      lines: [],
    }];
  }

  return sections.map((section, index) => ({
    id: Number.isSafeInteger(Number(section.id)) && Number(section.id) > 0 ? Number(section.id) : null,
    clientId: String(section.id ?? `section-${index}`),
    parentSectionId: section.parentSectionId || null,
    code: section.code || '',
    title: section.title,
    description: section.description || '',
    isClientVisible: section.isClientVisible !== false,
    orderIndex: section.orderIndex ?? index,
    lines: (section.lines || []).map((line, lineIndex) => serializeLine(
      { ...line, ...(calculatedLines.get(String(line.id)) || {}) },
      lineIndex
    )),
  }));
};

export const buildQuotePayload = (editor, calculated) => ({
  replaceSections: true,
  revision: editor.revision,
  quoteVersionId: editor.quoteVersionId || null,
  projectId: editor.projectId || null,
  affairId: editor.affairId || null,
  companyId: editor.companyId || null,
  quoteType: editor.quoteType || 'commercial',
  title: editor.title,
  status: editor.status,
  defaultLaborRate: calculated.settings.defaultLaborRate,
  defaultVatRate: calculated.settings.defaultVatRate,
  defaultMarginCoefficient: calculated.settings.defaultMarginCoefficient,
  ventilationMode: editor.defaultExpenseAllocationMode || editor.ventilationMode,
  roundingMode: calculated.settings.roundingMode,
  versionInformation: {
    objectText: editor.objectText,
    scopeText: editor.scopeText,
    exclusionsText: editor.exclusionsText,
    paymentTerms: editor.paymentTerms,
    paymentDelayDays: editor.paymentDelayDays === '' ? null : editor.paymentDelayDays,
    depositRate: editor.depositRate || 0,
    globalDiscountKind: editor.globalDiscountKind || 'percent',
    globalDiscountValue: editor.globalDiscountValue || 0,
    validityDays: editor.validityDays === '' ? 30 : Number(editor.validityDays ?? 30),
    executionDelay: editor.executionDelay,
    deliveryFormatCode: editor.deliveryFormatCode || 'PDF_ONLY',
    deliveryFormatOther: editor.deliveryFormatCode === 'OTHER' ? editor.deliveryFormatOther : null,
    siteVisitsIncluded: Boolean(editor.siteVisitsIncluded),
    siteVisitCount: editor.siteVisitsIncluded && editor.siteVisitCount !== '' ? Number(editor.siteVisitCount) : null,
    siteVisitConditions: editor.siteVisitsIncluded ? editor.siteVisitConditions : null,
    notesClient: editor.notesClient,
    notesInternal: editor.notesInternal,
    defaultVatRate: calculated.settings.defaultVatRate,
    currencyCode: assertEuro(editor.currencyCode),
    roundingMode: calculated.settings.roundingMode,
    ...(editor.customerSnapshot || {}),
  },
  versionParameters: editor.versionParameters || [],
  versionExpenses: editor.versionExpenses || [],
  paymentSchedule: (editor.paymentSchedule || []).map((item, index) => ({ ...item, orderIndex: index })),
  ...optionsPayload(editor),
  totals: calculated.totals,
  clientLines: calculated.clientLines.map(serializeLine),
  internalLines: calculated.internalLines.map(serializeLine),
  deletedLineIds: (editor.originalLineIds || []).filter(
    (id) => !flattenEditorLines(editor.sections).some((line) => Number(line.id) === Number(id))
  ),
  sections: serializeSections(editor, calculated),
});

// Cost-free editors submit commercial input only. Omitted components, options,
// parameters and expenses remain on the server; never serialize display defaults
// over hidden purchase costs or historical coefficient snapshots.
export const buildQuoteCommercialPayload = (editor) => {
  const fields = (value, keys) => Object.fromEntries(keys.filter((key) => value[key] !== undefined).map((key) => [key, value[key]]));
  return {
    ...fields(editor, ['revision', 'quoteVersionId', 'projectId', 'affairId', 'companyId', 'quoteType', 'title']),
    replaceSections: true,
    versionInformation: {
      ...fields(editor, ['objectText', 'scopeText', 'exclusionsText', 'paymentTerms', 'paymentDelayDays',
        'depositRate', 'globalDiscountKind', 'globalDiscountValue', 'validityDays',
        'executionDelay', 'deliveryFormatCode', 'deliveryFormatOther', 'siteVisitsIncluded', 'siteVisitCount',
        'siteVisitConditions', 'notesClient', 'notesInternal', 'defaultVatRate', 'roundingMode']),
      currencyCode: assertEuro(editor.currencyCode),
      ...(editor.customerSnapshot || {}),
    },
    paymentSchedule: (editor.paymentSchedule || []).map((item, orderIndex) => ({ ...item, orderIndex })),
    sections: (editor.sections || []).map((section, orderIndex) => ({
      ...fields(section, ['id', 'parentSectionId', 'code', 'title', 'description', 'isClientVisible']), orderIndex,
      lines: (section.lines || []).map((line, index) => ({
        ...fields(line, ['id', 'description', 'quantity', 'unit', 'lineType', 'isClientVisible', 'vatRate', 'discountKind', 'discountValue']),
        ...(editor.permissions?.canForcePrice && line.sellingPriceMode === 'forced' && line.forcedUnitSellingPrice != null
          ? fields(line, ['sellingPriceMode', 'forcedUnitSellingPrice', 'forceReason', 'forceExpiresAt']) : {}),
        ...(editor.permissions?.canForceCoefficient && line.coefficientMode === 'forced' && line.forcedCoefficient != null
          ? fields(line, ['coefficientMode', 'forcedCoefficient', 'forceReason', 'forceExpiresAt']) : {}),
        orderIndex: index,
      })),
    })),
  };
};

export const prefillEditorFromAffair = (editor, affair) => affair ? ({
  ...editor,
  affairId: affair.id,
  companyId: affair.companyId || affair.company_id || '',
  title: affair.name ? `Devis - ${affair.name}` : editor.title,
  affairAddress: [affair.addressLine1 || affair.address_line1, affair.addressLine2 || affair.address_line2, affair.postalCode || affair.postal_code, affair.city].filter(Boolean).join(', '),
  projectType: affair.projectType || affair.project_type || '',
  lots: affair.lots || affair.requirements?.lots || [],
}) : editor;
